import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSnackbar } from 'notistack';
import facturaVentaService, { type FacturaVenta, type FacturaVentaDetalle } from '../../../services/facturaVentaService';
import { formatCurrency, formatDateLocal } from '../../../utils/formatters';
import { getApiErrorMessage } from '../../../utils/apiError';
import { listarUltimasFacturasCliente } from './copiarDesdeFactura';

interface Props {
  open: boolean;
  nroCliente: number;
  onClose: () => void;
  onSelect: (factura: FacturaVenta) => void | Promise<void>;
}

function formatComprobante(factura: FacturaVenta): string {
  const letra = (factura.letra ?? '').trim();
  if (!letra || factura.nroSuc == null || factura.nroComprobante == null) {
    return `#${factura.nroFormulario}`;
  }
  const suc = String(factura.nroSuc).padStart(5, '0');
  const nro = String(factura.nroComprobante).padStart(8, '0');
  return `${letra.toUpperCase()}-${suc}-${nro}`;
}

function descuentoLineaPct(detalle: FacturaVentaDetalle): number {
  let porc = Number(detalle.porcDesc ?? detalle.descuento ?? 0);
  if (porc > 0 && porc <= 1) porc *= 100;
  if (!Number.isFinite(porc)) return 0;
  return Math.max(0, Math.min(100, porc));
}

function precioHistoricoLinea(detalle: FacturaVentaDetalle): number {
  const pu = Number(detalle.precioUnitario);
  if (Number.isFinite(pu) && pu > 0) return pu;
  const pl = Number(detalle.precioLista);
  if (Number.isFinite(pl) && pl > 0) return pl;
  return 0;
}

const FacturaPOSUltimasFacturasDialog: React.FC<Props> = ({
  open,
  nroCliente,
  onClose,
  onSelect,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [items, setItems] = useState<FacturaVenta[]>([]);
  const [expandedNro, setExpandedNro] = useState<number | null>(null);
  const [loadingDetalleNro, setLoadingDetalleNro] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    const nro = Number(nroCliente) || 0;
    if (nro <= 0) {
      setItems([]);
      setExpandedNro(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setExpandedNro(null);
    try {
      const list = await listarUltimasFacturasCliente(nro, 10);
      setItems(list);
    } catch (e) {
      setItems([]);
      enqueueSnackbar(getApiErrorMessage(e) || 'No se pudieron cargar las facturas del cliente.', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [nroCliente, enqueueSnackbar]);

  useEffect(() => {
    if (!open) {
      setExpandedNro(null);
      setLoadingDetalleNro(null);
      return;
    }
    void cargar();
  }, [open, cargar]);

  const asegurarDetalle = useCallback(async (factura: FacturaVenta) => {
    const nroFormulario = Number(factura.nroFormulario) || 0;
    if (nroFormulario <= 0) return;
    if (Array.isArray(factura.detalles) && factura.detalles.length > 0) return;

    setLoadingDetalleNro(nroFormulario);
    try {
      const detalle = await facturaVentaService.getFacturaVenta(nroFormulario);
      setItems((prev) =>
        prev.map((f) =>
          f.nroFormulario === nroFormulario
            ? { ...f, ...detalle, detalles: detalle.detalles ?? [] }
            : f,
        ),
      );
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e) || 'No se pudo cargar el detalle de la factura.', {
        variant: 'error',
      });
      setExpandedNro((cur) => (cur === nroFormulario ? null : cur));
    } finally {
      setLoadingDetalleNro(null);
    }
  }, [enqueueSnackbar]);

  const handleToggleDetalle = (factura: FacturaVenta) => {
    if (aplicando) return;
    const nro = Number(factura.nroFormulario) || 0;
    if (nro <= 0) return;
    if (expandedNro === nro) {
      setExpandedNro(null);
      return;
    }
    setExpandedNro(nro);
    void asegurarDetalle(factura);
  };

  const handleSelect = async (factura: FacturaVenta) => {
    if (aplicando) return;
    setAplicando(true);
    try {
      await onSelect(factura);
    } finally {
      setAplicando(false);
    }
  };

  const handleDialogClose = (
    _event?: object,
    _reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (aplicando) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={aplicando}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Últimas facturas del cliente</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Tocá una factura para ver el detalle. Con «Usar» se copian artículos, cantidades y descuentos
          con el precio actual de lista.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info">El cliente no tiene facturas recientes para copiar.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Comprobante</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Ítems</TableCell>
                  <TableCell align="right" width={120} />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((factura) => {
                  const nroFormulario = Number(factura.nroFormulario) || 0;
                  const expandida = expandedNro === nroFormulario;
                  const detalles = factura.detalles || [];
                  const cantItems = detalles.length;
                  const cargandoDetalle = loadingDetalleNro === nroFormulario;

                  return (
                    <React.Fragment key={nroFormulario}>
                      <TableRow
                        hover
                        selected={expandida}
                        onClick={() => handleToggleDetalle(factura)}
                        sx={{
                          cursor: aplicando ? 'default' : 'pointer',
                          '& > *': { borderBottom: expandida ? 'unset' : undefined },
                        }}
                      >
                        <TableCell sx={{ py: 0.5, width: 40 }}>
                          <IconButton
                            size="small"
                            aria-label={expandida ? 'Ocultar detalle' : 'Ver detalle'}
                            disabled={aplicando}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleDetalle(factura);
                            }}
                          >
                            {expandida ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatComprobante(factura)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateLocal(factura.fechaEmision) || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(Number(factura.importeTotal) || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {cantItems > 0 ? cantItems : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={aplicando}
                            onClick={() => void handleSelect(factura)}
                            sx={{ textTransform: 'none' }}
                          >
                            {aplicando ? <CircularProgress size={14} color="inherit" /> : 'Usar'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ py: 0, borderBottom: expandida ? undefined : 'none' }}
                        >
                          <Collapse in={expandida} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: { xs: 0.5, sm: 1 }, bgcolor: 'action.hover' }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 1 }}
                              >
                                Detalle de la factura (precios históricos; al usar se aplican precios actuales)
                              </Typography>
                              {cargandoDetalle ? (
                                <Box display="flex" justifyContent="center" py={2}>
                                  <CircularProgress size={22} />
                                </Box>
                              ) : detalles.length === 0 ? (
                                <Alert severity="info" sx={{ py: 0 }}>
                                  Esta factura no tiene líneas para mostrar.
                                </Alert>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Código</TableCell>
                                      <TableCell>Descripción</TableCell>
                                      <TableCell align="right">Cant.</TableCell>
                                      <TableCell align="right">Desc. %</TableCell>
                                      <TableCell align="right">Precio</TableCell>
                                      <TableCell align="right">Subtotal</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {detalles.map((d, idx) => {
                                      const cant = Number(d.cantidad) || 0;
                                      const desc = descuentoLineaPct(d);
                                      const precio = precioHistoricoLinea(d);
                                      const subtotalRaw = d.subtotalFinal ?? d.subtotal;
                                      const subtotalParsed =
                                        subtotalRaw == null ? NaN : Number(subtotalRaw);
                                      const subtotal = Number.isFinite(subtotalParsed)
                                        ? subtotalParsed
                                        : Math.round(cant * precio * (1 - desc / 100) * 100) / 100;
                                      return (
                                        <TableRow key={`${nroFormulario}-${d.nroItem ?? idx}`}>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {String(d.codigoArticulo || '').trim() || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {String(d.descripcionArticulo || '').trim() || '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">{cant || '—'}</Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">
                                              {desc > 0 ? `${desc}%` : '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">
                                              {precio > 0 ? formatCurrency(precio) : '—'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">
                                            <Typography variant="body2">
                                              {Number.isFinite(subtotal)
                                                ? formatCurrency(subtotal)
                                                : '—'}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} disabled={aplicando} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacturaPOSUltimasFacturasDialog;
